/**
 * Upload Queue Hook
 *
 * Manages the TUS-based resumable upload queue for mobile photo uploads.
 * Provides:
 * - Queue state management
 * - TUS upload handling with progress
 * - Automatic resume on visibility change and network restore
 * - Blob eviction recovery
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import * as tus from 'tus-js-client';
import * as uploadStorage from '../../../services/uploadStorage';
const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024; // 2MB
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000];
// Cross-tab coordination constants
const UPLOAD_LOCK_NAME = 'cfn-upload-queue';
const UPLOAD_CHANNEL_NAME = 'cfn-upload-channel';
// Create BroadcastChannel for cross-tab communication (if supported)
const uploadChannel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(UPLOAD_CHANNEL_NAME)
    : null;
export function useUploadQueue(options = {}) {
    const { maxConcurrent = 1, chunkSize = DEFAULT_CHUNK_SIZE, autoStart = true, onUploadComplete, onUploadError, } = options;
    const [queueItems, setQueueItems] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadMode, setUploadModeState] = useState(uploadStorage.getUploadMode);
    const [connectionType, setConnectionType] = useState(uploadStorage.getConnectionType);
    const activeUploads = useRef(new Map());
    const processingRef = useRef(false);
    const mountedRef = useRef(true);
    // Calculate whether uploads are blocked
    const isBlocked = !uploadStorage.shouldUploadNow();
    // Calculate overall progress
    const totalBytesUploaded = queueItems.reduce((sum, item) => sum + item.bytesUploaded, 0);
    const totalBytesTotal = queueItems.reduce((sum, item) => sum + item.bytesTotal, 0);
    const overallProgress = totalBytesTotal > 0 ? (totalBytesUploaded / totalBytesTotal) * 100 : 0;
    /**
     * Refresh queue state from IndexedDB
     */
    const refreshQueue = useCallback(async () => {
        if (!mountedRef.current)
            return;
        try {
            const items = await uploadStorage.getAllQueueItems();
            // Sort by createdAt (newest first for display, but process oldest first)
            items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            setQueueItems(items);
        }
        catch (err) {
            console.error('[UploadQueue] Failed to refresh queue:', err);
        }
    }, []);
    /**
     * Load queue on mount
     */
    useEffect(() => {
        mountedRef.current = true;
        refreshQueue();
        return () => {
            mountedRef.current = false;
        };
    }, [refreshQueue]);
    /**
     * Start a TUS upload for a queue item
     */
    const startUpload = useCallback(async (item) => {
        console.log('[UploadQueue] Starting upload:', item.fileId);
        // Check if already active
        if (activeUploads.current.has(item.fileId)) {
            console.log('[UploadQueue] Already uploading:', item.fileId);
            return;
        }
        // Verify item is still queued (may have been processed while waiting)
        const currentItem = await uploadStorage.getQueueItem(item.fileId);
        if (!currentItem || currentItem.status === 'uploading' || currentItem.status === 'completed') {
            console.log('[UploadQueue] Item not ready:', item.fileId, currentItem?.status);
            return;
        }
        // Mark as uploading and record start time
        const startedAt = Date.now();
        await uploadStorage.updateQueueItem(item.fileId, {
            status: 'uploading',
            startedAt,
        });
        await refreshQueue();
        // Rate limiting state - uses token bucket approach for smooth throttling
        // Track cumulative progress and compare against allowed progress at target rate
        const uploadStartTime = Date.now();
        let totalBytesSent = 0;
        // Use small fixed chunks for smooth progress updates (32KB = ~2.5s at 100kbps)
        const THROTTLED_CHUNK_SIZE = 32 * 1024;
        const initialBitrate = uploadStorage.getMaxBitrate();
        const effectiveChunkSize = initialBitrate > 0 ? THROTTLED_CHUNK_SIZE : chunkSize;
        if (effectiveChunkSize !== chunkSize) {
            console.log(`[UploadQueue] Using ${uploadStorage.formatBytes(effectiveChunkSize)} chunks for rate limiting`);
        }
        // Get blob from IndexedDB
        const blob = await uploadStorage.getBlob(item.fileId);
        if (!blob) {
            // Blob was evicted - mark for reselection
            console.warn('[UploadQueue] Blob missing for:', item.fileId);
            await uploadStorage.updateQueueItem(item.fileId, {
                status: 'needs_reselect',
                errorMessage: 'Photo data lost - please re-select the file',
            });
            await refreshQueue();
            return;
        }
        // Create TUS upload
        const upload = new tus.Upload(blob, {
            endpoint: '/tus',
            chunkSize: effectiveChunkSize,
            retryDelays: RETRY_DELAYS,
            metadata: {
                filename: item.filename,
                filetype: item.mimetype,
                objectType: item.objectType,
                objectId: String(item.objectId),
                fileId: item.fileId,
                browserLocation: item.browserLocation ? JSON.stringify(item.browserLocation) : '',
            },
            // Resume from previous URL if available
            uploadUrl: item.tusUrl || undefined,
            // Rate limiting using token bucket approach
            // Before each request, check if we're ahead of schedule and wait if needed
            onBeforeRequest: async (req) => {
                // Poll in small intervals to respond quickly to bitrate changes
                const POLL_INTERVAL = 250;
                while (true) {
                    const maxBitrate = uploadStorage.getMaxBitrate();
                    // No rate limit - proceed immediately
                    if (maxBitrate === 0)
                        break;
                    // Calculate how many bytes we're allowed to have sent by now
                    const elapsedMs = Date.now() - uploadStartTime;
                    const allowedBytes = (maxBitrate / 8) * (elapsedMs / 1000);
                    // If we haven't sent too much, proceed
                    if (totalBytesSent <= allowedBytes)
                        break;
                    // We're ahead of schedule - calculate wait time
                    const excessBytes = totalBytesSent - allowedBytes;
                    const waitNeeded = (excessBytes * 8 / maxBitrate) * 1000;
                    // Wait in small intervals to respond to bitrate changes
                    const waitTime = Math.min(POLL_INTERVAL, waitNeeded);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                }
            },
            // Track progress and save resume URL
            onAfterResponse: (req, res) => {
                // Save tusUrl for resume capability
                if (upload.url && upload.url !== item.tusUrl) {
                    console.log('[UploadQueue] Saving tusUrl for resume:', item.fileId);
                    uploadStorage.updateQueueItem(item.fileId, { tusUrl: upload.url });
                }
                // Update total bytes sent for rate limiting
                const currentOffset = upload._offset || 0;
                if (currentOffset > totalBytesSent) {
                    const chunkBytes = currentOffset - totalBytesSent;
                    totalBytesSent = currentOffset;
                    const maxBitrate = uploadStorage.getMaxBitrate();
                    if (maxBitrate > 0) {
                        const elapsedMs = Date.now() - uploadStartTime;
                        const currentRate = (totalBytesSent * 8) / (elapsedMs / 1000);
                        console.log(`[UploadQueue] Sent ${uploadStorage.formatBytes(totalBytesSent)}, rate: ${uploadStorage.formatBitrate(currentRate)} (target: ${uploadStorage.formatBitrate(maxBitrate)})`);
                    }
                }
                // Update chunk size if rate limit was added/removed
                const maxBitrate = uploadStorage.getMaxBitrate();
                const shouldThrottle = maxBitrate > 0;
                const currentChunkSize = upload.options.chunkSize;
                const desiredChunkSize = shouldThrottle ? THROTTLED_CHUNK_SIZE : chunkSize;
                if (currentChunkSize !== desiredChunkSize) {
                    upload.options.chunkSize = desiredChunkSize;
                    console.log(`[UploadQueue] Chunk size changed to ${uploadStorage.formatBytes(desiredChunkSize)}`);
                }
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                // Update progress in state
                if (mountedRef.current) {
                    setQueueItems(prev => prev.map(i => i.fileId === item.fileId
                        ? { ...i, bytesUploaded, bytesTotal, status: 'uploading' }
                        : i));
                }
                // Persist progress to IndexedDB and dispatch event for other components
                // (UploadHistoryPage polls IndexedDB, so we need frequent updates)
                uploadStorage.updateQueueItem(item.fileId, { bytesUploaded, bytesTotal });
                // Dispatch progress event for components that listen directly
                window.dispatchEvent(new CustomEvent('cfn:upload:progress', {
                    detail: { fileId: item.fileId, bytesUploaded, bytesTotal }
                }));
            },
            onSuccess: async () => {
                console.log('[UploadQueue] Upload complete:', item.fileId);
                // Record completion time and calculate effective bitrate
                const completedAt = Date.now();
                const durationMs = completedAt - startedAt;
                const durationSec = durationMs / 1000;
                const effectiveBitrate = durationSec > 0
                    ? Math.round((item.bytesTotal * 8) / durationSec)
                    : null;
                console.log(`[UploadQueue] Upload stats: ${uploadStorage.formatBytes(item.bytesTotal)} in ${durationSec.toFixed(1)}s = ${uploadStorage.formatBitrate(effectiveBitrate || 0)}`);
                // Parse upload ID from response headers
                let uploadId = 0;
                try {
                    // The upload ID is returned in the X-Upload-Id header
                    // Access via the internal xhr object
                    const xhr = upload._xhr;
                    if (xhr) {
                        const uploadIdHeader = xhr.getResponseHeader('X-Upload-Id');
                        if (uploadIdHeader) {
                            uploadId = parseInt(uploadIdHeader, 10);
                        }
                    }
                }
                catch (err) {
                    console.warn('[UploadQueue] Could not parse upload ID:', err);
                }
                // Update queue item with completion stats
                await uploadStorage.updateQueueItem(item.fileId, {
                    status: 'completed',
                    uploadId,
                    bytesUploaded: item.bytesTotal,
                    completedAt,
                    effectiveBitrate,
                });
                // Clean up blob
                await uploadStorage.deleteBlob(item.fileId);
                // Remove from active uploads
                activeUploads.current.delete(item.fileId);
                // Refresh state
                await refreshQueue();
                // Notify callback
                if (onUploadComplete) {
                    onUploadComplete(item, {
                        uploadId,
                        url: `/uploads/${item.objectType}/${item.objectId}/${item.filename}`,
                        filename: item.filename,
                    });
                }
                // Dispatch event for other components (legacy compatibility)
                window.dispatchEvent(new CustomEvent('cfn:upload:complete', {
                    detail: {
                        filename: item.filename,
                        upload: {
                            id: uploadId,
                            url: `/uploads/${item.objectType}/${item.objectId}/${item.filename}`,
                            filename: item.filename,
                            size: item.size,
                            mimetype: item.mimetype,
                            filedate: new Date().toISOString(),
                            UploadTags: [],
                        },
                    },
                }));
            },
            onError: async (error) => {
                console.error('[UploadQueue] Upload error:', error);
                // Determine error type
                const errorMessage = error.message || String(error);
                const isAuthError = errorMessage.includes('401') ||
                    errorMessage.includes('403') ||
                    errorMessage.includes('Authentication');
                const isNetworkError = errorMessage.includes('network') ||
                    errorMessage.includes('fetch') ||
                    errorMessage.includes('Failed to fetch');
                const isServerError = errorMessage.includes('500') ||
                    errorMessage.includes('502') ||
                    errorMessage.includes('503') ||
                    errorMessage.includes('504');
                // Auth errors are permanent failures, others should retry
                const newStatus = isAuthError ? 'failed' : 'queued';
                const errorCode = isAuthError ? 'AUTH_ERROR' :
                    isNetworkError ? 'NETWORK_ERROR' :
                        isServerError ? 'SERVER_ERROR' : 'UNKNOWN_ERROR';
                const newRetryCount = item.retryCount + 1;
                // If we've retried too many times, mark as paused for manual intervention
                const maxRetries = 10;
                const finalStatus = newRetryCount >= maxRetries ? 'paused' : newStatus;
                await uploadStorage.updateQueueItem(item.fileId, {
                    status: finalStatus,
                    tusUrl: upload.url || null, // Save for resume
                    errorCode,
                    errorMessage: errorMessage,
                    retryCount: newRetryCount,
                });
                activeUploads.current.delete(item.fileId);
                await refreshQueue();
                if (onUploadError) {
                    onUploadError(item, error instanceof Error ? error : new Error(errorMessage));
                }
                // If not a permanent failure and not at max retries, schedule a retry
                if (finalStatus === 'queued' && mountedRef.current) {
                    const delay = Math.min(5000 * newRetryCount, 30000); // Up to 30s delay
                    console.log(`[UploadQueue] Scheduling retry in ${delay}ms (attempt ${newRetryCount})`);
                    setTimeout(() => {
                        if (mountedRef.current && !processingRef.current) {
                            processQueue();
                        }
                    }, delay);
                }
            },
        });
        // Store reference
        activeUploads.current.set(item.fileId, upload);
        // Check for previous upload to resume
        upload.findPreviousUploads().then((previousUploads) => {
            if (previousUploads.length > 0) {
                console.log('[UploadQueue] Resuming from previous upload');
                upload.resumeFromPreviousUpload(previousUploads[0]);
            }
            // Start upload
            upload.start();
        });
        // Note: processQueue is intentionally not in deps - we use processingRef to check state
    }, [chunkSize, onUploadComplete, onUploadError, refreshQueue]);
    /**
     * Internal queue processing logic (called while holding the lock)
     */
    const processQueueInternal = useCallback(async () => {
        // First, reset any orphaned uploads (status='uploading' but not in activeUploads)
        // This must happen inside the lock to prevent interfering with another tab's uploads
        const allItems = await uploadStorage.getAllQueueItems();
        for (const item of allItems) {
            if (item.status === 'uploading' && !activeUploads.current.has(item.fileId)) {
                console.log('[UploadQueue] Resetting orphaned upload:', item.fileId);
                await uploadStorage.updateQueueItem(item.fileId, {
                    status: 'queued',
                    errorMessage: 'Upload interrupted - resuming',
                });
            }
        }
        await refreshQueue();
        // Now process the queue
        while (mountedRef.current) {
            // Get next item to upload
            const items = await uploadStorage.getPendingItems();
            const queuedItems = items.filter(item => item.status === 'queued' ||
                item.status === 'paused');
            if (queuedItems.length === 0) {
                console.log('[UploadQueue] No more items to process');
                break;
            }
            if (activeUploads.current.size >= maxConcurrent) {
                // Wait for an active upload to complete
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
            }
            const item = queuedItems[0];
            await startUpload(item);
            // Small delay between starting uploads
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }, [maxConcurrent, refreshQueue, startUpload]);
    /**
     * Process the upload queue with cross-tab coordination via Web Locks API.
     * Only one tab can process at a time - other tabs will skip if lock is held.
     */
    const processQueue = useCallback(async () => {
        if (processingRef.current)
            return;
        // Check if upload mode allows uploading now
        if (!uploadStorage.shouldUploadNow()) {
            const mode = uploadStorage.getUploadMode();
            console.log(`[UploadQueue] Upload blocked by mode: ${mode}`);
            return;
        }
        // Use Web Locks API if available for cross-tab coordination
        if (typeof navigator !== 'undefined' && navigator.locks) {
            // Try to acquire lock - if another tab has it, skip processing
            await navigator.locks.request(UPLOAD_LOCK_NAME, { ifAvailable: true }, async (lock) => {
                if (!lock) {
                    console.log('[UploadQueue] Another tab is processing uploads');
                    return;
                }
                // We have the lock - process the queue
                processingRef.current = true;
                setIsProcessing(true);
                console.log('[UploadQueue] Acquired lock, processing queue...');
                try {
                    await processQueueInternal();
                }
                finally {
                    processingRef.current = false;
                    if (mountedRef.current) {
                        setIsProcessing(false);
                    }
                }
            });
        }
        else {
            // Fallback for browsers without Web Locks API
            processingRef.current = true;
            setIsProcessing(true);
            console.log('[UploadQueue] Processing queue (no lock support)...');
            try {
                await processQueueInternal();
            }
            finally {
                processingRef.current = false;
                if (mountedRef.current) {
                    setIsProcessing(false);
                }
            }
        }
    }, [processQueueInternal]);
    /**
     * Add files to the queue
     * Delegates to the standalone addFilesToQueue function
     */
    const addFiles = useCallback(async (files, context) => {
        // Use the standalone function - it handles everything
        await uploadStorage.addFilesToQueue(files, context);
        // Refresh our local state
        await refreshQueue();
        // Notify other tabs that files were added (they may want to process)
        if (uploadChannel) {
            uploadChannel.postMessage({ type: 'files-added' });
        }
        // Auto-start if enabled
        if (autoStart && !processingRef.current) {
            processQueue();
        }
    }, [autoStart, processQueue, refreshQueue]);
    /**
     * Pause an upload
     */
    const pauseUpload = useCallback((fileId) => {
        console.log('[UploadQueue] Pausing upload:', fileId);
        const upload = activeUploads.current.get(fileId);
        if (upload) {
            upload.abort();
            activeUploads.current.delete(fileId);
        }
        uploadStorage.updateQueueItem(fileId, { status: 'paused' }).then(refreshQueue);
    }, [refreshQueue]);
    /**
     * Resume a paused upload
     */
    const resumeUpload = useCallback(async (fileId) => {
        console.log('[UploadQueue] Resuming upload:', fileId);
        const item = await uploadStorage.getQueueItem(fileId);
        if (item && (item.status === 'paused' || item.status === 'queued')) {
            await startUpload(item);
        }
    }, [startUpload]);
    /**
     * Remove an item from the queue
     */
    const removeItem = useCallback(async (fileId) => {
        console.log('[UploadQueue] Removing item:', fileId);
        // Abort if active
        const upload = activeUploads.current.get(fileId);
        if (upload) {
            upload.abort();
            activeUploads.current.delete(fileId);
        }
        await uploadStorage.removeFromQueue(fileId);
        await refreshQueue();
    }, [refreshQueue]);
    /**
     * Pause all uploads
     */
    const pauseAll = useCallback(() => {
        console.log('[UploadQueue] Pausing all uploads');
        activeUploads.current.forEach((upload, fileId) => {
            upload.abort();
            uploadStorage.updateQueueItem(fileId, { status: 'paused' });
        });
        activeUploads.current.clear();
        processingRef.current = false;
        setIsProcessing(false);
        refreshQueue();
    }, [refreshQueue]);
    /**
     * Resume all paused uploads
     */
    const resumeAll = useCallback(() => {
        console.log('[UploadQueue] Resuming all uploads');
        if (!processingRef.current) {
            processQueue();
        }
    }, [processQueue]);
    /**
     * Start processing the queue
     */
    const startQueue = useCallback(() => {
        if (!processingRef.current) {
            processQueue();
        }
    }, [processQueue]);
    /**
     * Re-select a file whose blob was evicted
     */
    const reselectFile = useCallback(async (fileId, file) => {
        console.log('[UploadQueue] Reselecting file:', fileId);
        await uploadStorage.saveBlob(fileId, file);
        await uploadStorage.updateQueueItem(fileId, {
            status: 'queued',
            errorCode: null,
            errorMessage: null,
        });
        await refreshQueue();
        if (autoStart && !processingRef.current) {
            processQueue();
        }
    }, [autoStart, processQueue, refreshQueue]);
    // Handle visibility change (pause/resume on app background/foreground)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // App went to background - uploads will pause naturally
                // Just persist current state
                console.log('[UploadQueue] App hidden, persisting state');
                queueItems.forEach(item => {
                    if (item.status === 'uploading') {
                        const upload = activeUploads.current.get(item.fileId);
                        if (upload?.url) {
                            uploadStorage.updateQueueItem(item.fileId, { tusUrl: upload.url });
                        }
                    }
                });
            }
            else {
                // App came to foreground - resume if auto-start enabled
                console.log('[UploadQueue] App visible, checking for uploads to resume');
                if (autoStart && !processingRef.current) {
                    processQueue();
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [autoStart, processQueue, queueItems]);
    // Handle online/offline events
    useEffect(() => {
        const handleOnline = () => {
            console.log('[UploadQueue] Network online, resuming uploads');
            if (autoStart && !processingRef.current) {
                processQueue();
            }
        };
        const handleOffline = () => {
            console.log('[UploadQueue] Network offline');
            // Uploads will fail naturally and be marked as paused
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [autoStart, processQueue]);
    // Listen for cross-tab messages via BroadcastChannel
    useEffect(() => {
        if (!uploadChannel)
            return;
        const handleMessage = (event) => {
            if (event.data?.type === 'files-added') {
                console.log('[UploadQueue] Files added from another tab, refreshing...');
                refreshQueue();
                // Try to process - will only succeed if we can acquire the lock
                if (autoStart && !processingRef.current) {
                    processQueue();
                }
            }
        };
        uploadChannel.addEventListener('message', handleMessage);
        return () => {
            uploadChannel.removeEventListener('message', handleMessage);
        };
    }, [autoStart, processQueue, refreshQueue]);
    // Listen for upload mode changes and connection type changes
    useEffect(() => {
        const handleModeChange = (event) => {
            const customEvent = event;
            const newMode = customEvent.detail?.mode ?? uploadStorage.getUploadMode();
            console.log('[UploadQueue] Upload mode changed:', newMode);
            setUploadModeState(newMode);
            // If mode now allows uploading and we have pending items, start processing
            if (uploadStorage.shouldUploadNow() && autoStart && !processingRef.current) {
                processQueue();
            }
        };
        const connection = navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;
        const handleConnectionChange = () => {
            const type = connection?.type ?? null;
            console.log('[UploadQueue] Connection type changed:', type);
            setConnectionType(type);
            // If we're now on a suitable connection and have pending items, start processing
            if (uploadStorage.shouldUploadNow() && autoStart && !processingRef.current) {
                processQueue();
            }
        };
        window.addEventListener('cfn:upload:mode-changed', handleModeChange);
        if (connection) {
            connection.addEventListener('change', handleConnectionChange);
        }
        return () => {
            window.removeEventListener('cfn:upload:mode-changed', handleModeChange);
            if (connection) {
                connection.removeEventListener('change', handleConnectionChange);
            }
        };
    }, [autoStart, processQueue]);
    // Listen for queue updates from other React roots (e.g., popups)
    useEffect(() => {
        const handleQueueUpdate = () => {
            console.log('[UploadQueue] Queue updated from another context, refreshing...');
            refreshQueue();
            // Also start processing if auto-start is enabled
            if (autoStart && !processingRef.current) {
                processQueue();
            }
        };
        window.addEventListener('cfn:upload:queued', handleQueueUpdate);
        return () => {
            window.removeEventListener('cfn:upload:queued', handleQueueUpdate);
        };
    }, [autoStart, processQueue, refreshQueue]);
    // Auto-start on mount if there are pending items
    useEffect(() => {
        const hasResumableItems = queueItems.some(i => i.status === 'queued' ||
            i.status === 'paused' ||
            // Include orphaned 'uploading' items (not in activeUploads)
            (i.status === 'uploading' && !activeUploads.current.has(i.fileId)));
        if (autoStart && hasResumableItems) {
            if (!processingRef.current) {
                processQueue();
            }
        }
    }, [autoStart, processQueue, queueItems.length]); // Only re-run when items are added
    return {
        queueItems,
        isProcessing,
        isBlocked,
        uploadMode,
        connectionType,
        addFiles,
        pauseUpload,
        resumeUpload,
        removeItem,
        pauseAll,
        resumeAll,
        startQueue,
        reselectFile,
        refreshQueue,
        overallProgress,
        totalBytesUploaded,
        totalBytesTotal,
    };
}
export default useUploadQueue;
